import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Users, Ticket, TrendingUp, ArrowRight, AlertTriangle, XCircle, Activity } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { StatusBadge } from "@/pages/admin/AdminLayout";
import { formatDateRange, categoryLabel } from "@/lib/format";

const StatCard = ({ label, value, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-noir-surface border border-noir-border p-6">
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">{label}</p>
      <Icon size={18} className="text-gold" />
    </div>
    <p className="mt-3 font-display text-5xl text-white">{value}</p>
  </div>
);

const ACTION_LABELS = {
  stage_created: "Nouveau stage créé",
  stage_updated: "Stage modifié",
  stage_archived: "Stage archivé",
  stage_deleted: "Stage supprimé",
  stage_duplicated: "Stage dupliqué",
  media_uploaded: "Photo ajoutée",
  media_video_added: "Vidéo ajoutée",
  media_updated: "Média modifié",
  media_deleted: "Média supprimé",
  sponsor_created: "Sponsor ajouté",
  sponsor_updated: "Sponsor modifié",
  sponsor_deleted: "Sponsor supprimé",
  excel_export: "Export Excel téléchargé",
};

const ProgressBar = ({ confirmed, capacity }) => {
  const pct = capacity ? Math.min(100, Math.round((confirmed / capacity) * 100)) : 0;
  const color = pct >= 100 ? "bg-rouge" : pct >= 80 ? "bg-gold" : "bg-royal-light";
  return (
    <div className="mt-3">
      <div className="flex justify-between text-[11px] text-white/50 mb-1.5">
        <span><span className="text-gold font-bold">{confirmed} / {capacity}</span> participants</span>
        <span>{capacity - confirmed} places restantes</span>
      </div>
      <div className="h-1.5 bg-white/10 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminApi.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) {
    return <p className="text-white/40">Chargement…</p>;
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <h1 className="font-display uppercase text-4xl tracking-wide">Dashboard</h1>

      {data.alerts?.length > 0 && (
        <div data-testid="dashboard-alerts" className="space-y-2">
          {data.alerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-5 py-3 border text-sm font-semibold ${
                a.level === "full"
                  ? "border-rouge/50 bg-rouge/10 text-rouge-hover"
                  : "border-gold/40 bg-gold/10 text-gold"
              }`}
            >
              {a.level === "full" ? <XCircle size={16} /> : <AlertTriangle size={16} />}
              {a.level === "full" ? "STAGE COMPLET — " : "STAGE PRESQUE COMPLET — "}{a.message}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Stages" value={data.total_stages} icon={CalendarDays} testId="stat-stages" />
        <StatCard label="Prochains stages" value={data.upcoming_stages_count} icon={TrendingUp} testId="stat-upcoming" />
        <StatCard label="Inscriptions confirmées" value={data.confirmed_registrations} icon={Users} testId="stat-confirmed" />
        <StatCard label="Places restantes" value={data.places_restantes_total} icon={Ticket} testId="stat-places" />
      </div>

      <section>
        <h2 className="font-display uppercase text-2xl tracking-wide text-gold mb-5">Prochains stages</h2>
        {data.upcoming_stages.length === 0 && <p className="text-white/40 text-sm">Aucun stage à venir.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.upcoming_stages.map((s) => (
            <Link
              key={s.id}
              to={`/admin/stages/${s.id}/modifier`}
              data-testid={`dashboard-stage-${s.slug}`}
              className="group bg-noir-surface border border-noir-border hover:border-gold transition-colors p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-display text-xl tracking-wide uppercase">{s.name}</p>
                <StatusBadge status={s.effective_status} />
              </div>
              <p className="mt-2 text-sm text-gold/80">{formatDateRange(s.start_date, s.end_date)}</p>
              <p className="text-xs text-white/50">{s.location} — {categoryLabel(s.categories)}</p>
              <ProgressBar confirmed={s.confirmed_count} capacity={s.capacity} />
            </Link>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display uppercase text-2xl tracking-wide text-gold">Dernières inscriptions</h2>
            <Link to="/admin/inscriptions" data-testid="dashboard-see-all" className="text-xs font-bold uppercase tracking-[0.15em] text-white/50 hover:text-gold flex items-center gap-2 transition-colors">
              Tout voir <ArrowRight size={13} />
            </Link>
          </div>
          {data.latest_registrations.length === 0 && (
            <p className="text-white/40 text-sm">Aucune inscription confirmée pour le moment.</p>
          )}
          <div className="bg-noir-surface border border-noir-border divide-y divide-white/5">
            {data.latest_registrations.map((r) => (
              <Link
                key={r.id}
                to={`/admin/inscriptions/${r.id}`}
                data-testid={`dashboard-reg-${r.id}`}
                className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-4 hover:bg-white/[0.03] transition-colors"
              >
                <span className="font-semibold text-sm">{r.child_first_name} {r.child_last_name}</span>
                <span className="text-xs text-white/50">{r.stage_name}</span>
                <span className="text-xs text-gold/80">{r.category}</span>
                <span className="text-xs text-white/40">{r.registration_reference}</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.15em] text-gold border border-gold/40 px-2 py-0.5">Payé</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display uppercase text-2xl tracking-wide text-gold mb-5 flex items-center gap-2">
            <Activity size={18} /> Activité récente
          </h2>
          <div className="bg-noir-surface border border-noir-border divide-y divide-white/5">
            {(data.recent_activity || []).length === 0 && (
              <p className="p-5 text-white/40 text-sm">Aucune activité enregistrée.</p>
            )}
            {(data.recent_activity || []).map((a, i) => (
              <div key={i} data-testid={`activity-${i}`} className="px-5 py-3.5 flex flex-wrap items-baseline gap-x-4">
                <span className="text-sm text-white">{ACTION_LABELS[a.action] || a.action}</span>
                <span className="text-xs text-white/40 truncate">{a.object}</span>
                <span className="ml-auto text-[10px] text-white/30">
                  {a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
